import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api/client';
import { config } from '@/lib/config';
import { Button } from '@/lib/ui/Button';
import { Dialog } from '@/lib/ui/Dialog';
import { useApiError } from '@/lib/query/useApiError';
import type { PreviewToken } from '@/lib/api/types';

/**
 * Preview is hidden in production until the draft renderer reaches live fidelity
 * (see the platform ticket FE-preview-live-fidelity). The implementation is
 * kept intact below — flip PREVIEW_ENABLED to true to restore the button.
 */
const PREVIEW_ENABLED = false;

/** Mint a preview token and open the draft render in EN/AR (spec §12). */
export function PreviewButton(props: { entityType: string; entityId: string }) {
  if (!PREVIEW_ENABLED) return null;
  return <PreviewButtonImpl {...props} />;
}

function PreviewButtonImpl({
  entityType,
  entityId,
}: {
  entityType: string;
  entityId: string;
}) {
  const { t } = useTranslation();
  const onError = useApiError();
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [locale, setLocale] = useState<'en' | 'ar'>('en');
  const [loading, setLoading] = useState(false);

  async function openPreview() {
    setLoading(true);
    try {
      const res = await api.post<PreviewToken>('/admin/preview-tokens', {
        entity_type: entityType,
        entity_id: entityId,
        ttl_minutes: 60,
      });
      setToken(res.token);
      setOpen(true);
    } catch (e) {
      onError(e);
    } finally {
      setLoading(false);
    }
  }

  // Point the iframe at the public website's preview *renderer* (not the raw API JSON endpoint):
  // the site route fetches the token payload server-side and renders the draft as HTML (FE-003).
  const src = token
    ? `${config.siteBaseUrl}/${locale}/preview/${encodeURIComponent(token)}`
    : '';

  return (
    <>
      <Button variant="ghost" size="sm" loading={loading} onClick={openPreview}>
        {t('common.preview')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen} title={t('preview.dialogTitle')} size="xl">
        <div className="mb-sm flex items-center gap-xs">
          {(['en', 'ar'] as const).map((l) => (
            <button
              key={l}
              onClick={() => setLocale(l)}
              className={
                'rounded-sm px-sm py-[4px] font-mono text-label uppercase tracking-[0.1em] ' +
                (locale === l ? 'bg-accent-blue text-canvas-paper' : 'bg-canvas-deep-sand text-ink-mute')
              }
            >
              {l}
            </button>
          ))}
        </div>
        {src && (
          <iframe
            title={t('preview.iframeTitle')}
            src={src}
            dir={locale === 'ar' ? 'rtl' : 'ltr'}
            sandbox="allow-same-origin allow-scripts"
            className="h-[70vh] w-full rounded-md border-[0.5px] border-hairline bg-canvas-paper"
          />
        )}
      </Dialog>
    </>
  );
}
